import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  Page,
  theme,
  typography,
} from "../Homepage/styles";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const PostDetailPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const PostDetailMain = styled.main`
  width: 100%;
  max-width: 100%;
  padding: 112px 16px 80px;
  overflow-x: clip;

  @media (min-width: 768px) {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

export const PostDetailLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 50vh;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  span {
    width: 28px;
    height: 28px;
    border: 2px solid hsl(var(--hp-border) / 0.8);
    border-top-color: ${theme.primary};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const PostDetailArticle = styled.article`
  width: 100%;
  max-width: 1024px;
  min-width: 0;
  margin: 0 auto;

  /* Old post used the full content width, so the video was watchable without fullscreen. */
  @media (min-width: 1280px) {
    max-width: 1400px;
  }
`;

export const PostDetailNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;
`;

export const PostDetailBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.75rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

export const PostDetailBackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;

  .material-icons {
    font-size: 16px;
  }

  &:hover {
    color: ${theme.primary};
    text-decoration: none;
  }
`;

export const PostDetailBreadcrumbSep = styled.span`
  color: hsl(var(--hp-border));
`;

export const PostDetailBreadcrumbCurrent = styled.span`
  color: ${theme.primary};
`;

export const PostDetailActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;

  @media (min-width: 640px) {
    gap: 8px;
  }
`;

/** Lovable postDetail like: gap-1.5 px-2 py-1.5 rounded-md text-sm */
export const PostDetailActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  border: none;
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background: transparent;
  color: ${({ $active }) => ($active ? theme.primary : theme.textMuted)};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  .material-icons {
    font-size: 20px;
  }

  &:hover {
    color: ${({ $active }) => ($active ? theme.primary : theme.foreground)};
    background: hsl(var(--hp-secondary));
  }

  ${({ $active }) =>
    $active &&
    css`
      &:hover {
        background: transparent;
      }
    `}
`;

/** Lovable bookmark: h-9 w-9 rounded-md; share trigger uses h-10 w-10 (Button size="icon"). */
export const PostDetailIconButton = styled(PostDetailActionButton)`
  width: 36px;
  height: 36px;
  min-height: 36px;
  padding: 0;

  ${({ $active }) =>
    $active &&
    css`
      color: ${theme.primary};
      background: hsl(var(--hp-primary) / 0.1);

      &:hover {
        color: ${theme.primary};
        background: hsl(var(--hp-primary) / 0.1);
      }
    `}
`;

export const PostDetailHeader = styled.header`
  margin-bottom: 40px;
`;

export const PostDetailCategoryBadge = styled.span`
  display: inline-flex;
  margin-bottom: 16px;
  padding: 4px 8px;
  border: 1px solid hsl(var(--hp-primary) / 0.3);
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.05);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.extrabold};
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

export const PostDetailTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const PostDetailCover = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  object-fit: cover;
  flex-shrink: 0;

  @media (min-width: 640px) {
    width: 208px;
  }

  @media (min-width: 768px) {
    width: 256px;
  }
`;

export const PostDetailTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.extrabold};
  line-height: 1.1;
  letter-spacing: ${typography.tracking.tight};

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

export const PostDetailMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

export const PostDetailAuthorBlock = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

export const PostDetailAuthorAvatar = styled.div`
  position: relative;
  flex-shrink: 0;

  img,
  .post-detail-author-fallback {
    display: block;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: 1px solid hsl(var(--hp-border) / 0.5);
    object-fit: cover;
    box-shadow: 0 0 0 4px hsl(var(--hp-background));
  }

  .post-detail-author-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: hsl(var(--hp-secondary));
    color: hsl(var(--hp-foreground));
    font-size: 1.125rem;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

export const PostDetailAuthorVerified = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid hsl(var(--hp-background));
  background: ${theme.primary};
  color: ${theme.primaryForeground};

  .material-icons {
    font-size: 10px;
  }
`;

export const PostDetailAuthorName = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${theme.foreground};
`;

export const PostDetailAuthorTitle = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

export const PostDetailMetaDivider = styled.div`
  display: none;
  width: 1px;
  height: 40px;
  background: hsl(var(--hp-border) / 0.5);

  @media (min-width: 768px) {
    display: block;
  }
`;

export const PostDetailDateBlock = styled.div`
  @media (min-width: 768px) {
    text-align: right;
  }
`;

export const PostDetailDateLabel = styled.p`
  margin: 0 0 4px;
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

export const PostDetailDateValue = styled.time`
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  color: ${theme.foreground};
`;

export const PostDetailVideoSection = styled.section`
  margin-bottom: 48px;
`;

export const PostDetailVideoFrame = styled.div`
  /* No border and no card shadow — both read as a gray ring on light bg. */
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 0 !important;
  border-radius: 12px;
  background: #000;
  box-shadow: none !important;
  outline: 0 !important;

  > .post-detail-video__embed,
  > .wistia_embed,
  > #wistia_player {
    display: block !important;
    width: 100% !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: 0 !important;
    background: #000 !important;
  }

  /* Poster state: fill the 16:9 cover. Playback lets Wistia size itself so the control bar is not cropped. */
  &:not(.is-playing) > .post-detail-video__embed,
  &:not(.is-playing) > .wistia_embed,
  &:not(.is-playing) > #wistia_player {
    position: absolute !important;
    inset: 0 !important;
    height: 100% !important;
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }

  &.is-playing {
    aspect-ratio: auto;
    height: auto;
    overflow: visible;

    > .post-detail-video__embed,
    > .wistia_embed,
    > #wistia_player {
      position: relative !important;
      height: auto !important;
    }
  }

  &:not(.is-playing) wistia-player,
  &:not(.is-playing) iframe,
  &:not(.is-playing) video {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    border: 0 !important;
    box-shadow: none !important;
    outline: 0 !important;
  }

  &.is-playing wistia-player,
  &.is-playing iframe,
  &.is-playing video {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    border: 0 !important;
    box-shadow: none !important;
    outline: 0 !important;
  }
`;

export const PostDetailVideoPlaceholder = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 12px;
  border: 0;
  background: #000;
  box-shadow: none;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PostDetailVideoPlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${theme.primary};
    color: ${theme.primaryForeground};
    box-shadow: 0 0 50px hsl(var(--hp-primary) / 0.3);
    transition: transform 0.2s ease;

    .material-icons {
      font-size: 32px;
      margin-left: 4px;
    }

    @media (min-width: 768px) {
      width: 80px;
      height: 80px;

      .material-icons {
        font-size: 36px;
      }
    }
  }
`;

/** Cover + circular play. No theme-background wash (that was the light-mode fade). */
export const PostDetailVideoPoster = styled.button`
  position: absolute;
  inset: 0;
  z-index: 2;
  margin: 0;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: #000;
  appearance: none;

  img,
  .post-detail-video__poster-fallback {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 1;
    transition: transform 0.25s ease, filter 0.25s ease;
  }

  .post-detail-video__poster-fallback {
    background: linear-gradient(
      145deg,
      hsl(var(--hp-secondary)) 0%,
      #111 100%
    );
  }

  /* Neutral dark scrim — same in light and dark (no white fade). */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
      to top,
      hsl(0 0% 0% / 0.45) 0%,
      hsl(0 0% 0% / 0.12) 40%,
      transparent 70%
    );
    pointer-events: none;
  }

  ${PostDetailVideoPlay} {
    z-index: 1;
  }

  &:hover img,
  &:focus-visible img {
    transform: scale(1.02);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: -2px;
  }

  &:hover ${PostDetailVideoPlay} span,
  &:focus-visible ${PostDetailVideoPlay} span {
    transform: scale(1.05);
  }
`;

export const PostDetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 48px;
  width: 100%;
  min-width: 0;

  @media (min-width: 1024px) {
    grid-template-columns: ${({ $hasSidebar }) =>
      $hasSidebar ? "minmax(0, 2fr) minmax(0, 1fr)" : "minmax(0, 1fr)"};
  }
`;

export const PostDetailContentCol = styled.div`
  min-width: 0;
  max-width: 100%;
`;

const richTextMediaContain = `
  overflow-wrap: anywhere;
  word-break: break-word;

  img,
  video,
  iframe,
  embed,
  object,
  svg {
    max-width: 100% !important;
    height: auto !important;
  }

  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  pre,
  code {
    max-width: 100%;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* CMS paste often leaves absolute widths on wrappers */
  div[style],
  p[style],
  span[style] {
    max-width: 100% !important;
  }
`;

export const PostDetailExcerpt = styled.div`
  margin: 0 0 40px;
  padding-left: 24px;
  border-left: 2px solid ${theme.primary};
  color: hsl(var(--hp-foreground) / 0.9);
  font-size: 1.25rem;
  font-style: italic;
  line-height: 1.6;
  max-width: 100%;
  min-width: 0;
  ${richTextMediaContain}

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }

  p {
    margin: 0;

    & + p {
      margin-top: 16px;
    }
  }
`;

export const PostDetailHtmlContent = styled.div`
  color: ${theme.textMuted};
  font-size: 1rem;
  line-height: 1.75;
  max-width: 100%;
  min-width: 0;
  ${richTextMediaContain}

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }

  h2,
  h3,
  h4 {
    margin: 40px 0 16px;
    color: ${theme.foreground};
    font-weight: ${typography.weights.bold};
    letter-spacing: ${typography.tracking.tight};
    text-transform: uppercase;
  }

  h2 {
    font-size: 1.375rem;

    @media (min-width: 768px) {
      font-size: 1.625rem;
    }
  }

  h3 {
    font-size: 1.25rem;

    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  h4 {
    font-size: 1.125rem;
    text-transform: none;
    letter-spacing: normal;

    @media (min-width: 768px) {
      font-size: 1.25rem;
    }
  }

  p {
    margin: 0 0 24px;
  }

  strong,
  b {
    color: hsl(var(--hp-foreground) / 0.92);
    font-weight: ${typography.weights.semibold};
  }

  a {
    color: ${theme.primary};
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.85;
      color: ${theme.primary};
    }
  }

  ul,
  ol {
    margin: 0 0 24px;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  blockquote {
    ${glassCardStyles}
    margin: 40px 0;
    padding: 24px;
    border-radius: 8px;
    border-color: hsl(var(--hp-border) / 0.5);

    @media (min-width: 768px) {
      padding: 32px;
    }

    &::before {
      content: "Bottom Line Narrative";
      display: block;
      margin-bottom: 12px;
      font-size: 0.625rem;
      font-weight: ${typography.weights.extrabold};
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: ${theme.primary};
      font-style: normal;
    }

    p {
      margin: 0;
      color: ${theme.foreground};
      font-size: 1.125rem;
      font-weight: ${typography.weights.medium};
      font-style: normal;
      line-height: 1.6;

      @media (min-width: 768px) {
        font-size: 1.25rem;
      }
    }
  }

  img {
    max-width: 100% !important;
    height: auto !important;
    border-radius: 6px;
  }

  iframe {
    max-width: 100% !important;
    border-radius: 6px;
  }
`;

export const PostDetailTagsFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid hsl(var(--hp-border) / 0.5);
`;

export const PostDetailTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const PostDetailTag = styled.span`
  display: inline-flex;
  padding: 4px 10px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 999px;
  color: ${theme.textMuted};
  font-size: 0.8125rem;
`;

export const PostDetailMetaStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  color: ${theme.textMuted};
  font-size: 0.75rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    svg {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }
  }
`;

export const PostDetailSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    align-self: start;
    position: sticky;
    top: 112px;
  }
`;

export const PostDetailGlassCard = styled.div`
  ${glassCardStyles}
  border-radius: 12px;
  padding: 24px;
`;

export const PostDetailSidebarTitle = styled.h4`
  margin: 0 0 24px;
  font-size: 0.625rem;
  font-weight: ${typography.weights.extrabold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

export const PostDetailSuggestedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PostDetailSuggestedLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none !important;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
    text-decoration: none !important;
  }

  time,
  h5,
  span,
  div {
    text-decoration: none !important;
  }

  time {
    display: block;
    margin-bottom: 6px;
    font-size: 0.625rem;
    font-weight: ${typography.weights.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.primary};
  }

  h5 {
    margin: 0 0 6px;
    font-size: 0.75rem;
    font-weight: ${typography.weights.bold};
    line-height: 1.5;
    color: ${theme.foreground};
    transition: color 0.2s ease;
  }

  &:hover h5 {
    color: ${theme.primary};
  }
`;

export const PostDetailSuggestedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PostDetailSuggestedAuthor = styled.span`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.625rem;
  font-weight: ${typography.weights.medium};
  color: ${theme.textMuted};
`;

export const PostDetailSuggestedType = styled.span`
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 999px;
  font-size: 0.5625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

export const PostDetailGateCard = styled.div`
  ${glassCardStyles}
  border-radius: calc(var(--hp-radius, 0.5rem) + 4px);
  padding: 32px 24px;
  border-color: hsl(var(--hp-primary) / 0.2);
  text-align: center;

  @media (min-width: 768px) {
    padding: 48px;
  }
`;

export const PostDetailGateIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
  border: 1px solid hsl(var(--hp-primary) / 0.25);
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.08);
  color: ${theme.primary};

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

export const PostDetailGateTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 1.5rem;
  font-weight: ${typography.weights.extrabold};
  letter-spacing: ${typography.tracking.tight};
  color: ${theme.foreground};

  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

export const PostDetailGateText = styled.p`
  margin: 0 auto 28px;
  max-width: 28rem;
  color: ${theme.textMuted};
  line-height: 1.6;
`;

/** Left-aligned copy for the public email unlock form (not centered like membership gates). */
export const PostDetailEmailGateText = styled(PostDetailGateText)`
  margin: 0 0 28px;
  max-width: none;
  text-align: left;
`;

export const PostDetailGateActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const postDetailButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  border-radius: var(--hp-radius, 0.75rem);
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid hsl(var(--hp-primary));
    outline-offset: 2px;
  }
`;

/** Lovable postDetail: Button variant="hero" + text-[10px] font-black uppercase tracking-[0.2em] px-8 */
export const PostDetailHeroButton = styled.button`
  ${postDetailButtonBase}
  min-height: 40px;
  padding: 8px 32px;
  border: none;
  background-image: var(--hp-gradient-gold);
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: hsl(var(--hp-primary-foreground));
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
  }

  &:active {
    transform: scale(0.98);
  }
`;

/** Lovable sidebar: w-full py-5 + hero overrides */
export const PostDetailSidebarCta = styled(PostDetailHeroButton)`
  width: 100%;
  min-height: auto;
  padding: 20px 32px;
`;

/** Lovable postDetail: Button variant="outline" + font-black uppercase overrides */
export const PostDetailOutlineButton = styled(Link)`
  ${postDetailButtonBase}
  min-height: 40px;
  padding: 8px 32px;
  border: 1px solid hsl(var(--hp-border));
  background: transparent;
  color: hsl(var(--hp-foreground));
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;

  &:hover {
    background: hsl(var(--hp-secondary));
    border-color: hsl(var(--hp-primary) / 0.5);
    color: hsl(var(--hp-foreground));
    text-decoration: none;
  }
`;

export const PostDetailEmailForm = styled.form`
  ${glassCardStyles}
  border-radius: 12px;
  padding: 32px;
  border-color: hsl(var(--hp-border) / 0.6);
  text-align: left;
`;

export const PostDetailEmailFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 28rem;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }

  input {
    flex: 1;
    height: 40px;
    min-height: 40px;
    padding: 8px 12px;
    border: 1px solid hsl(var(--hp-border));
    border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
    background: hsl(var(--hp-background));
    color: hsl(var(--hp-foreground));
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25;

    &::placeholder {
      color: hsl(var(--hp-muted-foreground));
    }

    &:focus-visible {
      outline: none;
      border-color: hsl(var(--hp-ring));
      box-shadow: 0 0 0 2px hsl(var(--hp-background)), 0 0 0 4px hsl(var(--hp-ring) / 0.35);
    }
  }

  button[type="submit"] {
    flex-shrink: 0;
  }
`;

export const PostDetailEmailPreview = styled.div`
  position: relative;
  max-width: 100%;
  min-width: 0;
  max-height: 256px;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0;
    height: 128px;
    background: linear-gradient(to top, hsl(var(--hp-background)), transparent);
  }
`;

export const PostDetailFeaturedRibbon = styled.span`
  display: inline-flex;
  margin-bottom: 16px;
  padding: 4px 10px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.6875rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PostDetailNotFound = styled.div`
  padding: 160px 16px 80px;
  text-align: center;

  h1 {
    margin: 0 0 16px;
    font-size: 1.875rem;
    font-weight: ${typography.weights.bold};
  }

  p {
    margin: 0 0 32px;
    color: ${theme.textMuted};
  }
`;

export const PostDetailLoader = styled.div`
  padding: 160px 16px;
  text-align: center;
  color: ${theme.textMuted};
`;

export const PostDetailShareWrap = styled.div`
  position: relative;
`;

export const PostDetailCommentsSlot = styled.div`
  margin-top: 48px;

  /* Tags footer above already has a divider when nested in the main column. */
  ${({ $nested }) =>
    $nested
      ? ""
      : `
    padding-top: 40px;
    border-top: 1px solid hsl(var(--hp-border) / 0.5);
  `}
`;
